/**
 * Resolve and decode user context from multiple sources.
 * Priority:
 *  1) userContextB64 query param on current window URL
 *  2) userContextB64 query param on parent window URL (if accessible)
 *  3) window.__CFC_PLAYER_DATA__.userContextB64
 */

function getQueryParamFromSearch(search, key) {
  if (!search) return undefined
  try {
    const params = new URLSearchParams(search)
    return params.get(key) || undefined
  } catch (_err) {
    return undefined
  }
}

function getQueryParamFromLocationLike(locationLike, key) {
  if (!locationLike) return undefined
  try {
    // Prefer href when available
    const href =
      typeof locationLike.href === 'string' ? locationLike.href : undefined
    if (href) {
      const url = new URL(href)
      const val = url.searchParams.get(key)
      if (val) return val
    }
  } catch (_err) {
    // ignore and fallback to search
  }
  try {
    const search =
      typeof locationLike.search === 'string' ? locationLike.search : undefined
    if (search) {
      return getQueryParamFromSearch(search, key)
    }
  } catch (_err) {
    // ignore
  }
  return undefined
}

function getCurrentWindowSearch() {
  try {
    return window?.location?.search || ''
  } catch (_err) {
    return ''
  }
}

function getParentWindowSearch() {
  try {
    // Access may throw in cross-origin iframes
    if (
      typeof window !== 'undefined' &&
      window.parent &&
      window.parent !== window
    ) {
      return window.parent.location?.search || ''
    }
  } catch (_err) {
    // ignore
  }
  return ''
}

function getPlayerDataUserContextB64() {
  try {
    const playerData = window?.__CFC_PLAYER_DATA__
    return playerData?.userContextB64 || undefined
  } catch (_err) {
    return undefined
  }
}

function normalizeBase64(b64) {
  if (!b64 || typeof b64 !== 'string') return undefined
  let normalized = b64.replace(/\s+/g, '')
  // Convert URL-safe base64 to standard
  normalized = normalized.replace(/-/g, '+').replace(/_/g, '/')
  // Pad with '=' to a multiple of 4
  const padLen = normalized.length % 4
  if (padLen) {
    normalized = normalized + '='.repeat(4 - padLen)
  }
  return normalized
}

function base64ToUtf8(b64) {
  const normalized = normalizeBase64(b64)
  if (!normalized) return undefined
  try {
    if (typeof atob === 'function') {
      // atob decodes to a binary string; convert to UTF-8
      const binary = atob(normalized)
      // Decode percent-encoded UTF-8
      const bytes = Array.prototype.map
        .call(binary, ch => ch.charCodeAt(0))
        .map(byte => `%${`0${byte.toString(16)}`.slice(-2)}`)
        .join('')
      return decodeURIComponent(bytes)
    }
  } catch (_err) {
    // Fallback to Buffer
  }
  try {
    // eslint-disable-next-line no-undef
    if (typeof Buffer !== 'undefined') {
      // eslint-disable-next-line no-undef
      return Buffer.from(normalized, 'base64').toString('utf8')
    }
  } catch (_err) {
    // ignore
  }
  return undefined
}

function parseJson(jsonStr) {
  if (!jsonStr) return undefined
  try {
    return JSON.parse(jsonStr)
  } catch (_err) {
    return undefined
  }
}

export function getUserContextFrom(win) {
  // 1) current window param
  const fromWindow = getQueryParamFromLocationLike(
    win?.location,
    'userContextB64',
  )
  if (fromWindow) {
    const json = base64ToUtf8(fromWindow)
    const obj = parseJson(json)
    if (obj) return obj
  }

  // 2) parent window param
  const fromParent = (() => {
    try {
      if (win?.parent) {
        return getQueryParamFromLocationLike(
          win.parent.location,
          'userContextB64',
        )
      }
    } catch (_err) {
      // ignore
    }
    return undefined
  })()
  if (fromParent) {
    const json = base64ToUtf8(fromParent)
    const obj = parseJson(json)
    if (obj) return obj
  }

  // 3) __CFC_PLAYER_DATA__.userContextB64
  const fromPlayerData = (() => {
    try {
      return win?.__CFC_PLAYER_DATA__?.userContextB64
    } catch (_err) {
      return undefined
    }
  })()
  if (fromPlayerData) {
    const json = base64ToUtf8(fromPlayerData)
    const obj = parseJson(json)
    if (obj) return obj
  }

  return undefined
}

export function getUserContext() {
  return getUserContextFrom(typeof window !== 'undefined' ? window : undefined)
}

// Export internals for testing where necessary
export const __testables = {
  getQueryParamFromSearch,
  getQueryParamFromLocationLike,
  getCurrentWindowSearch,
  getParentWindowSearch,
  getPlayerDataUserContextB64,
  normalizeBase64,
  base64ToUtf8,
  parseJson,
}
